import React, { useMemo, useState, useRef, useEffect } from "react";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ThreeCircles } from "react-loader-spinner";
import * as Icon from "react-bootstrap-icons";
import { ColDef } from "ag-grid-community";
import styles from "./ApprovalDetailsPage.module.css";
import TemplatePreviewModal from "../template_preview_modal/TemplatePreviewModal";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import ActionsRenderer from "./components/ActionsRenderer/ActionsRenderer";
import ClientNameRenderer from "./components/ClientNameRenderer/ClientNameRenderer";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";
import SummaryModal from "../SummaryModal/SummaryModal";
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import {
  fetchApprovalPageData,
  saveEngagementLetterApprovalDetails,
} from "./ApprovalDetailsPage.service";
import { parseDeepLinkFromUrl, updateDisabledReason } from "./ApprovalDetailsPage.utils";
import {
  CygnusGridRef,
  normalizeServerFilters,
} from "../../../pages/reusable_components/CygnusGrid/CygnusGrid.utils";
import { useGlobalStore } from "../../store/globalStore";
import {
  createPartnerProvider,
  createPurposeProvider,
  createUsersProvider,
  createClientTypesProvider,
  createStatusProvider,
  createLabelMap,
} from "../../../pages/reusable_components/CygnusGrid/CygnusGrid.utils";
import { fetchFirmReferenceData } from "../../firm_setup/FieldMappings/FieldMappings.service";
import { ApprovalMessageModal } from "../../client_onboarding/ApprovalMessageModal";

export interface ELApprovalGridProps {
  transporter: any;
  deepLink?: { tab?: string; recordId?: string; action?: string };
}

/**
 * Renders the approval details page, showing engagement letter approval data and actions.
 *
 * This component includes:
 * - Display of approval details in a grid.
 * - Action buttons for approving or rejecting engagement letters.
 * - Modals for template preview and message confirmation.
 * - Loader to indicate grid data refresh.
 *
 * @component
 * @returns {JSX.Element} The rendered approval details page with a grid, action buttons, and modals
 */
const ApprovalDetailsPage: React.FC<ELApprovalGridProps> = ({ deepLink, transporter }) => {
  const [refreshGrid, setRefreshGrid] = useState<boolean>(false);
  const [userName] = useState<string | null>(localStorage.getItem("username"));
  const [userRole] = useState<string | null>(localStorage.getItem("usertype"));
  const [messageModalShow, setMessageModalShow] = useState<boolean>(false);
  const rejectLetterButtonRef = useRef<HTMLDivElement>(null);
  const [approveStatus, setApproveStatus] = useState<boolean>(false);
  const [rejectStatus, setRejectStatus] = useState<boolean>(false);
  const [templatePreviewModal, setTemplatePreviewModal] =
    useState<boolean>(false);
  const [pdfFileUrl, setPdfFileUrl] = useState<string | undefined>();
  const [approveLetterDisabledReason, setApproveLetterDisabledReason] =
    useState<string | null>(null);
  const [rejectLetterDisabledReason, setRejectLetterDisabledReason] = useState<
    string | null
  >(null);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [elId, setELId] = useState<string>("");
  const [elFileName, setELFileName] = useState<string>("");
  const {
    purposeOptions,
    primaryPartnerOptions,
    officeLocationOptions,
    users,
    clientTypes,
    elStatus,
    elSignatureStatus,
  } = useGlobalStore();
  const gridRef = useRef<CygnusGridRef>(null);
  const [rejectOptions, setRejectOptions] = useState<any[]>([]);

  const allSetFilterValuesByKey = useMemo(() => {
    const values = (items: any[], selector: (item: any) => unknown) =>
      Array.from(new Set((items ?? []).map(selector).map((value) => String(value ?? "").trim()).filter(Boolean)));
    const partners = values(primaryPartnerOptions, (item) => item?.value);

    return {
      ClientType: values(clientTypes, (item) => item?.ReferenceDataValue),
      OfficeLocation: values(officeLocationOptions, (item) => item?.value),
      BusinessUnit: values(purposeOptions, (item) => item?.value ?? item?.code),
      PrimaryPartner: partners,
      OriginatingPartner: partners,
      LetterStatus: values(elStatus, (item) => item?.ReferenceDataValue),
      SignatureStatus: values(elSignatureStatus, (item) => item?.ReferenceDataValue),
      CreatedBy: values(users, (item) => item?.UserFullName),
      PendingOn: ["Approval", "COI Conflict"],
    };
  }, [clientTypes, officeLocationOptions, purposeOptions, primaryPartnerOptions, elStatus, elSignatureStatus, users]);

  const allSetFilterValuesByKeyRef = useRef(allSetFilterValuesByKey);
  allSetFilterValuesByKeyRef.current = allSetFilterValuesByKey;

  // Style for the AgGrid
  const gridStyle = useMemo(() => ({ height: "85vh", width: "100%" }), []);

  const textParams = { debounceMs: 300, caseSensitive: false };

  const officeLabelByVal = createLabelMap(
    officeLocationOptions,
    (o) => o.value,
    (o) => o.label
  );
  const partnerLabelByVal = createLabelMap(
    primaryPartnerOptions,
    (o) => o.value,
    (o) => o.label
  );
  const losLabelByVal = createLabelMap(
    purposeOptions,
    (o) => String(o.value ?? o.code),
    (o) => o.label
  );

  const getOfficeLocationValues = createPartnerProvider(officeLocationOptions);
  const getPartnerValues = createPartnerProvider(primaryPartnerOptions);
  const getLosValues = createPurposeProvider(purposeOptions);
  const getUserValues = createUsersProvider(users);
  const getClientTypeValues = createClientTypesProvider(clientTypes);
  const getStatusValues = createStatusProvider(elStatus);
  const getSignatureStatusValues = createStatusProvider(elSignatureStatus);
  const [gridReady, setGridReady] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const openApprovalForRow = (row: any) => {
    const api = gridRef.current?.api;
    if (!api || !row) return;

    let targetNode: any = null;
    api.forEachNode((node: any) => {
      if (
        node.data?.ELApprovalId === row.ELApprovalId ||
        node.data?.ProcessedTemplateId === row.ProcessedTemplateId
      ) {
        targetNode = node;
      }
    });

    if (targetNode) {
      api.deselectAll();
      targetNode.setSelected(true);
      api.ensureNodeVisible(targetNode, "middle");
      console.log("🎯 Selected node:", targetNode.data);
    } else {
      console.warn("⚠️ No matching node found for:", row);
    }
  };

  useEffect(() => {
    const dl = deepLink ?? parseDeepLinkFromUrl();
    if (!dl?.recordId || dl.action !== "openApproval") return;
    if (!gridReady || !dataLoaded) return;

    const api = gridRef.current?.api;
    if (!api) return;

    const wanted = String(dl.recordId).trim();
    const wantedNum = Number(wanted);

    const allRows: any[] = [];
    api.forEachNode((node) => allRows.push(node.data));

    const hit =
      allRows.find(
        (d) =>
          String(d?.ELApprovalId) === wanted ||
          String(d?.ELApprovalId) === String(wantedNum) ||
          String(d?.ProcessedTemplateId) === wanted ||
          String(d?.ProcessedTemplateId || "").endsWith(wanted)
      ) || null;

    if (hit) {
      openApprovalForRow(hit);

      // Clean the URL so the selection doesn’t repeat on refresh
      const url = new URL(window.location.href);
      url.searchParams.delete("recordId");
      url.searchParams.delete("action");
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", url.toString());
    } else {
      console.warn("⚠️ Deep link record not found in current grid:", wanted);
    }
  }, [deepLink, gridReady, dataLoaded]);

  // Default column definitions for the grid
  const defaultApprovalDetailsColumnDefs: ColDef[] = [
    {
      field: "__select",
      colId: "select",
      headerName: "",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      width: 50,
      maxWidth: 50,
      filter: false,
      headerClass: "ag-center-select-header",
      pinned: "left",
    },
    {
      headerName: "Client Id",
      field: "ClientId",
      colId: "clientId",
      sortable: false,
      minWidth: 200,
      filter: "agTextColumnFilter",
      filterParams: textParams,
    },
    {
      headerName: "Client Name",
      field: "ClientName",
      colId: "clientName",
      minWidth: 260,
      sortable: false,
      filter: "agTextColumnFilter",
      filterParams: textParams,
      cellRenderer: ClientNameRenderer,
      cellRendererParams: { transporter },
    },
    {
      headerName: "Client Type",
      field: "ClientType",
      colId: "clientType",
      filter: "agSetColumnFilter",
      minWidth: 140,
      filterParams: {
        values: getClientTypeValues,
        refreshValuesOnOpen: true,
      },
    },
    {
      field: "PendingOn",
      colId: "pendingOn",
      headerName: "Pending On",
      sortable: false,
      minWidth: 130,
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["Approval", "COI Conflict"],
        refreshValuesOnOpen: true,
      },
    },
    {
      field: "OfficeLocation",
      colId: "officeLocation",
      headerName: "Office Location",
      sortable: false,
      minWidth: 130,
      filter: "agSetColumnFilter",
      filterParams: {
        values: getOfficeLocationValues,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) => officeLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        officeLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      field: "BusinessUnit",
      colId: "businessUnit",
      headerName: "Line of Service",
      sortable: false,
      minWidth: 120,
      filter: "agSetColumnFilter",
      filterParams: {
        values: getLosValues,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) => losLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        losLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      field: "PrimaryPartner",
      colId: "primaryPartner",
      headerName: "Primary Partner",
      minWidth: 180,
      sortable: false,
      filter: "agSetColumnFilter",
      filterParams: {
        values: getPartnerValues,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) =>
          partnerLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      field: "OriginatingPartner",
      colId: "originatingPartner",
      headerName: "Originating Partner",
      minWidth: 180,
      sortable: false,
      filter: "agSetColumnFilter",
      filterParams: {
        values: getPartnerValues,
        refreshValuesOnOpen: true,
        valueFormatter: (p: any) =>
          partnerLabelByVal.get(String(p.value)) ?? "",
      },
      valueFormatter: (p) =>
        partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
    },
    {
      headerName: "Delivery Email",
      field: "ClientEmail",
      minWidth: 160,
      colId: "clientEmail",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Letter Status",
      field: "LetterStatus",
      colId: "letterStatus",
      minWidth: 140,
      filter: "agSetColumnFilter",
      filterParams: {
        values: getStatusValues,
        refreshValuesOnOpen: true,
      },
    },
    {
      headerName: "Signature Status",
      field: "SignatureStatus",
      colId: "signatureStatus",
      filter: "agSetColumnFilter",
      minWidth: 140,
      filterParams: {
        values: getSignatureStatusValues,
        refreshValuesOnOpen: true,
      },
    },
    {
      headerName: "Created By",
      field: "CreatedBy",
      colId: "createdBy",
      minWidth: 180,
      filter: "agSetColumnFilter",
      filterParams: {
        values: getUserValues,
        refreshValuesOnOpen: true,
      },
    },
    {
      headerName: "Created Date",
      field: "CreatedDate",
      colId: "createdDate",
      minWidth: 160,
      cellRenderer: TimeRenderer,
      cellRendererParams: (params: any) => ({
        value: params.value,
      }),
      filter: false,
    },
    {
      headerName: "Updated Date",
      field: "UpdatedDate",
      colId: "updatedDate",
      minWidth: 160,
      cellRenderer: TimeRenderer,
      cellRendererParams: (params: any) => ({
        value: params.value,
      }),
      filter: false,
    },
    {
      headerName: "Action",
      field: "",
      pinned: "right",
      maxWidth: 80,
      minWidth: 80,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        setPdfFileUrl,
        setTemplatePreviewModal,
        setIsSummaryModalOpen,
        setELId,
        setELFileName,
        transporter,
      },
      sortable: false,
      filter: false,
    },
  ];

  const [columnDefs, setColumnDefs] = useState<ColDef[]>(
    defaultApprovalDetailsColumnDefs
  );

  useEffect(() => {
    updateDisabledReason(
      selectedRows,
      "approve",
      setApproveLetterDisabledReason
    );
    updateDisabledReason(selectedRows, "reject", setRejectLetterDisabledReason);
  }, []);

  // Function to handle row selection change
  const onSelectionChanged = (selectedRows: any[]) => {
    setSelectedRows(selectedRows);
  };

  useEffect(() => {
    updateDisabledReason(
      selectedRows,
      "approve",
      setApproveLetterDisabledReason
    );
    updateDisabledReason(selectedRows, "reject", setRejectLetterDisabledReason);
  }, [selectedRows]);

  const handleSaveApproval = async (
    approvalStatus: "Approved" | "Rejected",
    message: string = ""
  ) => {
    if (selectedRows.length === 0) return;

    approvalStatus === "Approved"
      ? setApproveStatus(true)
      : setRejectStatus(true);

    try {
      const success = await saveEngagementLetterApprovalDetails(
        selectedRows,
        approvalStatus,
        message
      );

      if (success) {
        setTimeout(() => {
          gridRef.current?.refreshGrid();
        }, 1000);
      } else {
      }
    } catch (error) {
    } finally {
      approvalStatus === "Approved"
        ? setApproveStatus(false)
        : setRejectStatus(false);
    }
  };

  // Calculate the position of the modal based on the button's position
  const buttonPosition = rejectLetterButtonRef.current
    ? rejectLetterButtonRef.current.getBoundingClientRect()
    : { top: 0, left: 0, bottom: 0 };

  const approveLetterBtnClick = async () => {
    await handleSaveApproval("Approved");
  };

  const rejectLetterBtnClick = () => {
    setMessageModalShow(true);
  };
  
  useEffect(() => {
    async function loadRejectOptions() {
      try {
        const data = await fetchFirmReferenceData("EngagementLetterRejectionOptions");
        const values =
          Array.isArray(data)
            ? data
                .map((item) => item?.ReferenceDataValue)
                .filter((v) => v !== undefined && v !== null)
            : [];
        setRejectOptions(values || []);
      } catch (e) {
        console.error("Failed to load Engagement Letter Rejection Options", e);
      }
    }

    loadRejectOptions();
  }, []);

  // JSX for the page component
  return (
    <div className={styles.approvalDetailsPage}>
      {isSummaryModalOpen && (
        <SummaryModal
          isOpen={isSummaryModalOpen}
          onClose={() => setIsSummaryModalOpen(false)}
          elId={elId}
          elFileName={elFileName}
        />
      )}

      <ApprovalMessageModal
        show={messageModalShow}
        rejectOptions={rejectOptions}
        onHide={() => setMessageModalShow(false)}
        approvalcontinuation={(message: string) => {
          setMessageModalShow(false);
          handleSaveApproval("Rejected", message)
        }}
      />
      
      {templatePreviewModal && (
        <TemplatePreviewModal
          show={templatePreviewModal}
          pdfFileUrl={pdfFileUrl}
          onHide={() => setTemplatePreviewModal(false)}
        />
      )}
      <Row className="no-gutter">
        <p className={`display-6 ${styles.approvalDetailsHeading}`}>
          Approval Details
        </p>
      </Row>
      <Row className="no-gutter">
        <Col
          md={12}
          className={`d-flex justify-content-end ${styles.actionButtonsContainer}`}
        >
          <div className={styles.actionButton}>
            <OverlayTrigger
              placement="bottom"
              overlay={
                approveLetterDisabledReason ? (
                  <Tooltip id="button-tooltip">
                    {approveLetterDisabledReason}
                  </Tooltip>
                ) : (
                  <></>
                )
              }
            >
              <div className="d-inline-block">
                <CustomButton
                  disabled={!!approveLetterDisabledReason}
                  progress={approveStatus}
                  onclick={approveLetterBtnClick}
                  content={"Approve Letter"}
                />
              </div>
            </OverlayTrigger>
          </div>
          <div className={styles.actionButton} ref={rejectLetterButtonRef}>
            <OverlayTrigger
              placement="bottom"
              overlay={
                rejectLetterDisabledReason ? (
                  <Tooltip id="button-tooltip">
                    {rejectLetterDisabledReason}
                  </Tooltip>
                ) : (
                  <></>
                )
              }
            >
              <div className="d-inline-block">
                <CustomButton
                  disabled={!!rejectLetterDisabledReason}
                  progress={rejectStatus}
                  onclick={rejectLetterBtnClick}
                  content={"Reject Letter"}
                />
              </div>
            </OverlayTrigger>
          </div>
          <div className={styles.refreshIconWrapper}>
            {refreshGrid ? (
              <div>
                <ThreeCircles
                  height="20"
                  width="20"
                  color="#4fa94d"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel="three-circles-rotating"
                  outerCircleColor="var(--theme1_tertiary)"
                  innerCircleColor="var(--theme1_secondary)"
                  middleCircleColor="var(--theme1_primary)"
                />
              </div>
            ) : (
              <Icon.ArrowCounterclockwise
                onClick={() => gridRef.current?.refreshGrid()}
                className={styles.refreshIcon}
              />
            )}
          </div>
        </Col>
      </Row>
      <Row className="no-gutter">
        <Col md={12}>
          <div className="ag-theme-balham" style={gridStyle}>
            <CygnusGrid
              ref={gridRef}
              fetchData={async ({ offset, limit, filter }) => {
                const res = await fetchApprovalPageData(userName!, userRole!, {
                  offset,
                  limit,
                  filter: normalizeServerFilters(
                    filter,
                    allSetFilterValuesByKeyRef.current
                  ),
                });

                // Ensure CygnusGrid receives correct structure
                return {
                  records: res?.records || res || [],
                  totalCount: res?.totalCount || 0,
                };
              }}
              dataIdentifier="ELApprovalId"
              columnDefs={columnDefs}
              rowSelection={"multiple"}
              onSelectionChange={onSelectionChanged}
              onLoadingChange={setRefreshGrid}
              floatingFilters={true}
              onGridReady={() => {
                setGridReady(true);
              }}
              onFirstDataRendered={() => {
                setDataLoaded(true);
              }}
              filterKeyMap={{
                clientId: "ClientId",
                clientName: "ClientName",
                clientEmail: "ClientEmail",
                clientType: "ClientType",
                officeLocation: "OfficeLocation",
                businessUnit: "BusinessUnit",
                primaryPartner: "PrimaryPartner",
                originatingPartner: "OriginatingPartner",
                approvalStatus: "ApprovalStatus",
                letterStatus: "LetterStatus",
                signatureStatus: "SignatureStatus",
                createdBy: "CreatedBy",
                pendingOn: "PendingOn",
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ApprovalDetailsPage;
